import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/me';
import Modal from './Modal';

/**
 * 마감일 공지 팝업(2026-11-04 way) — 「마감 전에 공지팝업 · 마감일 13시·15시 두 번 · 작업을 서둘러 달라」.
 *
 * 원천 = `close_deadline`(listCloseDeadlines). 기한 **당일**(KST) 13:00·15:00 이 지나면 뜬다.
 * 두 시각이 다 지난 뒤 처음 열었으면 한 번만(「확인」은 지나간 시각을 전부 확인 처리).
 * 받는 사람 = 그 기한에 막히는 사람:
 *   영업   = 관리자·회계 외 전원(서버 assertDeadlineOpen 과 같은 면제)
 *   월정기 = 관리자·회계 외 + 월정기 화면 입력 권한(인차팀)
 *   회계   = 회계(accounting) 역할 — 막히지는 않고 알림만
 * 「확인」 기억 = 이 브라우저(localStorage). 다른 PC 에선 한 번 더 뜰 수 있다(무해).
 */

const SLOTS = [13, 15] as const;
const ACK_KEY = 'close-deadline-ack-v1';
const FETCH_MS = 10 * 60_000;
const TICK_MS = 30_000;

type Row = { driveMon: string; kind: string; deadline: string; closed: boolean };

function kstNow(): { day: string; hour: number } {
  const d = new Date(Date.now() + 9 * 3600_000);
  return { day: d.toISOString().slice(0, 10), hour: d.getUTCHours() };
}
function readAck(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(ACK_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}
function writeAck(keys: string[]) {
  try {
    const s = readAck();
    keys.forEach((k) => s.add(k));
    localStorage.setItem(ACK_KEY, JSON.stringify([...s].slice(-60)));
  } catch { /* 못 쓰면 다음 박자에 다시 뜬다 */ }
}

const AREA: Record<string, string> = {
  영업: '배차·배정, 통근 운행일수, 접수·노선, 영업정산',
  월정기: '월정기 등록·취소·수정',
};

function message(r: Row): { title: string; body: string } {
  const [, m] = r.driveMon.split('-').map(Number);
  const [, dm, dd] = r.deadline.split('-').map(Number);
  if (r.kind === '회계') {
    return {
      title: `오늘은 ${m}월 회계 마감일입니다`,
      body: `오늘(${dm}월 ${dd}일)이 ${m}월 회계 마감일입니다. 남은 마감 작업을 서둘러 마무리해 주세요.`,
    };
  }
  const area = AREA[r.kind] ?? r.kind;
  return {
    title: `오늘은 ${m}월 ${r.kind === '영업' ? '영업 정산' : r.kind} 마감일입니다`,
    body: `오늘(${dm}월 ${dd}일)이 ${m}월 ${r.kind === '영업' ? '영업 정산' : r.kind} 마감일입니다. 오늘 밤 12시가 지나면 ${m}월분 ${area}을 더 이상 고칠 수 없습니다. 남은 작업을 서둘러 마무리해 주세요.`,
  };
}

export function CloseDeadlineAlert() {
  const { me } = useMe();
  const rows = useRef<Row[]>([]);
  const [pop, setPop] = useState<{ row: Row; keys: string[] }[] | null>(null);

  useEffect(() => {
    if (!me) return;
    const exempt = me.role === 'admin' || me.role === 'accounting';
    const mine = (r: Row) => {
      if (r.kind === '영업') return !exempt;
      if (r.kind === '월정기') return !exempt && me.screens?.['result-mo'] === 'write';
      if (r.kind === '회계') return me.role === 'accounting';
      return false;
    };
    let alive = true;
    const check = () => {
      const { day, hour } = kstNow();
      const ack = readAck();
      const due: { row: Row; keys: string[] }[] = [];
      for (const r of rows.current) {
        if (r.deadline !== day || !mine(r)) continue;
        const keys = SLOTS.filter((h) => hour >= h).map((h) => `${r.driveMon}|${r.kind}|${r.deadline}|${h}`);
        if (keys.some((k) => !ack.has(k))) due.push({ row: r, keys });
      }
      setPop(due.length ? due : null);
    };
    const load = () => {
      api.listCloseDeadlines({}).then((res) => {
        if (!alive) return;
        rows.current = res.rows as Row[];
        check();
      }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
    };
    load();
    const f = setInterval(load, FETCH_MS);
    const t = setInterval(check, TICK_MS);
    window.addEventListener('focus', check);
    return () => { alive = false; clearInterval(f); clearInterval(t); window.removeEventListener('focus', check); };
  }, [me]);

  if (!pop) return null;
  const close = () => { writeAck(pop.flatMap((p) => p.keys)); setPop(null); };
  return (
    <Modal width="max-w-lg">
      <div className="card space-y-3 p-5">
        {pop.map(({ row }) => {
          const msg = message(row);
          return (
            <div key={`${row.driveMon}|${row.kind}`} className="space-y-1">
              <div className="text-lg font-bold">⏰ {msg.title}</div>
              <p className="text-sm text-ink-soft">{msg.body}</p>
            </div>
          );
        })}
        <div className="flex justify-end">
          <button className="btn-primary" onClick={close}>확인</button>
        </div>
      </div>
    </Modal>
  );
}
